import { NavLink } from "react-router-dom";
import { Box, Stack, UnstyledButton, Group, Text, Badge, Avatar, Divider } from "@mantine/core";
import { useAuth } from "../../../hooks/core/useAuth";
import {
  IconDashboard,
  IconUser,
  IconListDetails,
  IconUsers,
  IconCar,
  IconBuildingStore,
  IconMap,
  IconMap2,
  IconSettings,
  IconBuilding,
  IconHistory,
  IconBell,
  IconCoin,
  IconShieldLock,
  IconClipboardData,
  IconCalendarEvent,
  IconUserCog,
  IconBriefcase,
  IconLogout,
  IconTools,
  IconDatabase,
} from "@tabler/icons-react";

const navItems = [
  { icon: IconDashboard, label: "Dashboard", path: "/" },
  { icon: IconCar, label: "Bookings", path: "/bookings" },
  { icon: IconCalendarEvent, label: "Schedules", path: "/schedules" },
  { icon: IconBuildingStore, label: "Accounts", path: "/accounts" },
  { icon: IconUser, label: "Customers", path: "/customers" },
  { icon: IconUsers, label: "Drivers", path: "/drivers" },
  { icon: IconClipboardData, label: "Live Logs", path: "/live-logs" },

  // Administration section
  { icon: IconShieldLock, label: "Auth Management", path: "/auth-management" },
  { icon: IconUserCog, label: "Staff Management", path: "/staff-management" },
  { icon: IconSettings, label: "Config Management", path: "/config-management" },
  { icon: IconDatabase, label: "Data Migration", path: "/dev-tools/data-migration" },
  { icon: IconTools, label: "Ghost Documents", path: "/dev-tools/ghost-documents" },

  // { icon: IconMap2, label: "Live Map", path: "/live-map" },
  { icon: IconCoin, label: "Finance", path: "/finance" },
  { icon: IconMap, label: "Regions", path: "/regions" },
  { icon: IconHistory, label: "Audit Logs", path: "/audit-logs" },
  { icon: IconBell, label: "Notifications", path: "/notifications" },
  { icon: IconBell, label: "Notifications Monitor", path: "/notifications-v2", description: "Enhanced notifications" },
  { icon: IconBriefcase, label: "My Profile", path: "/staff/profile", highlight: true },
];

export function Sidebar() {
  const { staffProfile, user, isLoading, logout } = useAuth();

  // Get user information with proper fallbacks
  const userName = staffProfile?.displayName || user?.email?.split("@")[0] || "User";
  const userRole = staffProfile?.role || "Staff";
  const userDepartment = staffProfile?.department || "";

  return (
    <Box
      w={250}
      h="100%"
      p="md"
      bg="gray.0"
      style={{
        borderRight: "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Stack gap="sm" style={{ flex: 1 }}>
        {navItems.map((item) => (
          <UnstyledButton
            component={NavLink}
            to={item.path}
            key={item.label}
            p="xs"
            styles={(theme) => ({
              root: {
                borderRadius: theme.radius.sm,
                color: theme.colors.gray[7],
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.colors.gray[1],
                  transform: "translateX(4px)",
                },
                "&.active": {
                  backgroundColor: theme.colors.gray[1],
                  color: theme.colors.blue[7],
                  borderLeft: `3px solid ${theme.colors.blue[7]}`,
                  paddingLeft: theme.spacing.xs,
                  "& svg": {
                    color: theme.colors.blue[7],
                  },
                },
              },
            })}
          >
            <Group position="apart">
              <Group>
                <item.icon
                  size={20}
                  style={{
                    color: item.highlight ? "var(--mantine-color-blue-6)" : "var(--mantine-color-gray-6)",
                  }}
                />
                <Box>
                  <Text size="sm" fw={item.highlight ? 600 : 400}>
                    {item.label}
                  </Text>
                  {item.description && (
                    <Text size="xs" color="dimmed">
                      {item.description}
                    </Text>
                  )}
                </Box>
              </Group>
              {item.highlight && (
                <Badge size="xs" variant="light" color="blue">
                  Profile
                </Badge>
              )}
            </Group>
          </UnstyledButton>
        ))}
      </Stack>

      {/* Logout button at the bottom */}
      <UnstyledButton
        onClick={async () => {
          const result = await logout();
          if (result?.success) {
            window.location.href = "/auth"; // Redirect to login page after logout
          }
        }}
        p="xs"
        mt="auto"
        styles={(theme) => ({
          root: {
            borderRadius: theme.radius.sm,
            color: theme.colors.red[6],
            transition: "all 0.2s ease",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.colors.red[0],
              transform: "translateX(4px)",
            },
          },
        })}
      >
        <Group>
          <IconLogout size={20} />
          <Text size="sm" fw={500}>
            Logout
          </Text>
        </Group>
      </UnstyledButton>
    </Box>
  );
}

export default Sidebar;
